import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import type { BlockBodyAnchor } from '../blockBodyAnchor';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';
import AnimatedCheckmark from '@/components/ora/Global/AnimatedCheckmark';
import { parseHtmlWithMathLatex } from '@/utils/htmlParser';
import { Button } from '@/components/ui/button';

type FlashcardsBlockBodyProps = {
  anchor?: BlockBodyAnchor;
  autoShown?: boolean;
  items: Array<Record<string, unknown>>;
  onAnchorChange: (anchor: BlockBodyAnchor) => void;
};

export const FlashcardsBlockBody = ({
  anchor,
  autoShown,
  items,
  onAnchorChange,
}: FlashcardsBlockBodyProps) => {
  const flashcardAnchor = anchor?.flashcard as
    Record<string, unknown> | undefined;

  const initialIndex = Math.max(
    0,
    items.findIndex((item) => getString(item.id) === getString(anchor?.id))
  );

  const [index, setIndex] = useState(initialIndex);
  const [showBack, setShowBack] = useState(flashcardAnchor?.face === 'back');
  const [isDone, setIsDone] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the block on mount when the assistant auto-opened it, so spacebar
  // works immediately without a click.
  useEffect(() => {
    if (autoShown) containerRef.current?.focus();
  }, [autoShown]);

  const updateAnchor = (
    targetItem: Record<string, unknown> | undefined,
    targetShowBack: boolean
  ) => {
    if (!targetItem) return;

    onAnchorChange({
      type: 'flashcard',
      id: getString(targetItem.id),
      flashcard: { face: targetShowBack ? 'back' : 'front' },
    });
  };

  if (!items.length) {
    return <div className="chat-ui_flashcard">No flashcards in this turn.</div>;
  }

  const item = items[index];
  const front = getString(item?.front) || '(front missing)';
  const back = getString(item?.back) || '(back missing)';
  const explanation = getString(item?.explanation_short);
  const isLast = index === items.length - 1;

  const reveal = () => {
    if (showBack) return;

    setShowBack(true);
    updateAnchor(item, true);
  };

  const goNext = () => {
    if (!showBack) return;

    if (isLast) {
      setIsDone(true);
      return;
    }

    const next = index + 1;
    setIndex(next);
    setShowBack(false);
    updateAnchor(items[next], false);
  };

  const redo = () => {
    setIsDone(false);
    setIndex(0);
    setShowBack(false);
    updateAnchor(items[0], false);
    containerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.code !== 'Space') return;
    // Only handle space when the block itself is focused; let nested controls
    // (buttons) handle their own activation to avoid double-firing.
    if (event.target !== event.currentTarget) return;

    event.preventDefault();
    if (isDone) return;
    if (showBack) goNext();
    else reveal();
  };

  return (
    <div
      aria-label="Flashcards"
      className="chat-ui_flashcard"
      onKeyDown={handleKeyDown}
      ref={containerRef}
      role="group"
      tabIndex={0}
    >
      {isDone ? (
        <div className="chat-ui_flashcard_done">
          <div className="chat-ui_flashcard_done_check">
            <AnimatedCheckmark />
          </div>
          <p>
            You reviewed {items.length}{' '}
            {items.length === 1 ? 'flashcard' : 'flashcards'}.
          </p>
          <Button className="w-[32rem] max-w-full" onClick={redo} type="button">
            Redo Flashcards
          </Button>
        </div>
      ) : (
        <>
          <span className="chat-ui_flashcard_label">
            {index + 1} / {items.length}
          </span>
          <div className="chat-ui_flashcard_card">
            {!showBack ? (
              <div className="chat-ui_flashcard_face">
                {parseHtmlWithMathLatex(front)}
              </div>
            ) : null}
            {showBack ? (
              <div className="chat-ui_flashcard_face chat-ui_flashcard_face--back">
                {parseHtmlWithMathLatex(back)}
                {explanation ? (
                  <p className="chat-ui_flashcard_explanation">
                    {parseHtmlWithMathLatex(explanation)}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <footer className="chat-ui_flashcard_footer">
            {showBack ? (
              <Button
                className="w-[32rem] max-w-full"
                onClick={goNext}
                type="button"
              >
                {isLast ? 'Finish' : 'Next'}
              </Button>
            ) : (
              <Button
                className="w-[32rem] max-w-full"
                onClick={reveal}
                type="button"
              >
                Reveal
              </Button>
            )}
          </footer>
        </>
      )}
    </div>
  );
};
